// PKCE/Magic-Link: Muss vor App/Supabase laufen (siehe Modul-Kommentar).
import "./lib/auth/recoverPkceLandingToCallback";
import { registerChunkLoadRecovery } from "@/utils/chunkLoadRecovery";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

try {
  sessionStorage.removeItem("impersonation_state");
} catch {}

// Sentry initialisieren (so früh wie möglich)
// DSGVO-konform: DSN aus ENV, keine Default-PII, Noise-Reduzierung
// 🚫 DEAKTIVIERT auf localhost: Fehler werden nur in der Browser-Konsole angezeigt
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
// Prüfe, ob wir auf localhost sind (für lokale Entwicklung)
const isLocalhost =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "");

if (SENTRY_DSN && !isLocalhost) {
  import("@sentry/react").then(Sentry => {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment:
        (import.meta.env.VITE_SENTRY_ENV as string) ||
        (import.meta.env.MODE as string) ||
        "development",
      release: import.meta.env.VITE_APP_VERSION || "unknown",
      sendDefaultPii: false,
      normalizeDepth: 8,
      // Alle Errors erfassen (auch außerhalb Prod, damit Tests/Staging vollständig sind)
      sampleRate: 1.0,
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "NetworkError when attempting to fetch resource.",
      ],
      denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
      integrations: [
        Sentry.breadcrumbsIntegration({
          console: true,
          dom: true,
          fetch: true,
          history: true,
          xhr: true,
        }),
      ],
      beforeBreadcrumb(breadcrumb) {
        // Console-Spam (debug/log) rausfiltern, nur warn/error behalten
        if (
          breadcrumb.category === "console" &&
          breadcrumb.level !== "warning" &&
          breadcrumb.level !== "error"
        ) {
          return null;
        }
        return breadcrumb;
      },
    });
  });
} else if (isLocalhost && SENTRY_DSN) {
  console.log(
    "🔇 Sentry deaktiviert: Lokale Entwicklung auf localhost erkannt"
  );
}

// Error Reporting & Debug Logging initialisieren

// Browser-Extension-Fehler unterdrücken (runtime.lastError)
// Diese Fehler kommen von Browser-Extensions, nicht von unserem Code
if (
  typeof window !== "undefined" &&
  typeof chrome !== "undefined" &&
  chrome.runtime
) {
  // Unterdrücke runtime.lastError Warnungen in der Konsole
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    const message = args[0]?.toString() || "";
    // Ignoriere runtime.lastError Fehler von Browser-Extensions
    if (
      message.includes("runtime.lastError") ||
      message.includes("message channel closed")
    ) {
      return; // Unterdrücke diesen Fehler
    }
    originalError.apply(console, args);
  };
}

// Debug-Konsole für Browser-Konsole laden
import "./config/debug-console";

// Remote Debug Client initialisieren
// 🚨 SICHERHEIT: Nur in Development-Modus, niemals in Production!
// ⚠️ TEMPORÄR DEAKTIVIERT - kann später wieder aktiviert werden
// if (
//   import.meta.env.DEV &&
//   import.meta.env.MODE !== "production" &&
//   !import.meta.env.PROD
// ) {
//   import("./lib/remote-debug-client").catch(() => {
//     // Ignoriere Fehler wenn nicht verfügbar
//   });
// }

// Development/Test: Network safety guard gegen produktive Supabase-Aufrufe
if (
  import.meta.env.MODE !== "production" &&
  import.meta.env.VITE_ALLOW_PROD !== "true"
) {
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : ((input as Request).url ?? String(input));
    if (
      /https?:\/\/[^/]*\.supabase\.co/i.test(url) &&
      !/localhost|127\.0\.0\.1/.test(url)
    ) {
      const errorMessage = `🚫 BLOCKIERT: Production Supabase-Aufruf in Development-Modus.\n\nURL: ${url}\n\nLösung: Setze VITE_ALLOW_PROD=true in deiner .env Datei, wenn du bewusst gegen Production arbeiten möchtest.\n\nAlternativ: Verwende eine lokale Supabase-Instanz (z.B. http://localhost:54321).`;
      console.error(errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
    return origFetch(input as any, init as any);
  };
}

// Coverage Client initialisieren (nur in Coverage-Modus) - DEAKTIVIERT
// import { initCoverageClient } from "./lib/coverage-client";

// Stelle sicher, dass das DOM-Element existiert, bevor wir versuchen, darauf zuzugreifen
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element nicht gefunden. Überprüfen Sie Ihr HTML.");
}

registerChunkLoadRecovery();

// Verwende createRoot mit expliziten Typen
const root = createRoot(rootElement);

// Coverage Client starten (falls aktiviert) - DEAKTIVIERT
// initCoverageClient();

// Service Worker Provider für PWA Updates
import { ServiceWorkerProvider } from "./components/ServiceWorkerProvider";
// Theme Provider für Dark Mode
import { ThemeProvider } from "./hooks/useTheme";

// Auto-Fullscreen beim ersten Tap aktivieren (nur in PWA)
import { enableAutoFullscreenOnFirstTap } from "./utils/fullscreen";
enableAutoFullscreenOnFirstTap();

// OAuth: PKCE-Rückkehr von Google in die Capacitor-WebView (nicht im Chrome-Tab hängen bleiben)
import { registerCapacitorOAuthDeepLink } from "./lib/auth/capacitorOAuthDeepLink";
registerCapacitorOAuthDeepLink();

// Globaler Handler: Verhindere Standard-Kontextmenü auf Desktop (nicht auf Touch-Geräten)
// Auf Touch-Geräten gibt es kein Kontextmenü durch Rechtsklick
if (typeof document !== "undefined") {
  // Prüfe, ob es sich um ein Touch-Gerät handelt
  const isTouchDevice =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - für ältere Browser
    navigator.msMaxTouchPoints > 0;

  // Nur auf Desktop (nicht-Touch) das Kontextmenü verhindern
  if (!isTouchDevice) {
    document.addEventListener(
      "contextmenu",
      (e: Event) => {
        const target = e.target as HTMLElement;

        // Erlaube Kontextmenü in Text-Inputs, Textareas und Content-Editable
        const isTextInput =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable ||
          target.closest("input, textarea, [contenteditable]");

        // Erlaube Kontextmenü in bestimmten Bereichen (falls explizit erlaubt)
        const isAllowedArea = target.closest("[data-allow-context-menu]");

        // Wenn es kein Text-Input ist und nicht explizit erlaubt, verhindere Kontextmenü
        if (!isTextInput && !isAllowedArea) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      { capture: true } // Capture-Phase, damit es vor anderen Handlern greift
    );
  }
}

// Render die App
root.render(
  <StrictMode>
    <ThemeProvider defaultTheme="dark" storageKey="coupleup-theme">
      <ServiceWorkerProvider>
        <App />
      </ServiceWorkerProvider>
    </ThemeProvider>
  </StrictMode>
);
